import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from '../hooks/use-toast';

const CartContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const { user, token } = useAuth();
  const { toast } = useToast();

  // Fetch cart from backend
  const fetchCart = useCallback(async () => {
    if (!user || !token) {
      setCartItems([]);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/cart`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCartItems(data.items || []);
      } else {
        setCartItems([]);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  }, [user, token]);

  // Load cart when user logs in
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Add item to cart
  const addToCart = async (product, priceType = 'normal') => {
    if (!user || !token) {
      toast({
        title: 'Error',
        description: 'Debes iniciar sesión para agregar productos al carrito',
        variant: 'destructive',
      });
      return false;
    }

    try {
      const response = await fetch(`${API_URL}/api/cart/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          product_id: product.id,
          price_type: priceType,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCartItems(data.items || []);
        toast({
          title: '¡Añadido al carrito!',
          description: `${product.url || product.website} se ha añadido al carrito`,
        });
        return true;
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'No se pudo añadir al carrito',
          variant: 'destructive',
        });
        return false;
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: 'Error',
        description: 'Error de conexión',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Remove item from cart
  const removeFromCart = async (itemId) => {
    if (!user || !token) return false;

    try {
      const response = await fetch(`${API_URL}/api/cart/items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCartItems(data.items || []);
        toast({
          title: 'Eliminado',
          description: 'Producto eliminado del carrito',
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing from cart:', error);
      return false;
    }
  };

  // Update item price type
  const updateItemPriceType = async (itemId, priceType) => {
    if (!user || !token) return false;

    try {
      const response = await fetch(`${API_URL}/api/cart/items/${itemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ price_type: priceType }),
      });

      if (response.ok) {
        const data = await response.json();
        setCartItems(data.items || []);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating cart item:', error);
      return false;
    }
  };

  // Clear cart
  const clearCart = async () => {
    if (!user || !token) return false;

    try {
      const response = await fetch(`${API_URL}/api/cart`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setCartItems([]);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error clearing cart:', error);
      return false;
    }
  };

  // Checkout - create orders
  const checkout = async (customerData = {}) => {
    if (!user || !token) {
      toast({
        title: 'Error',
        description: 'Debes iniciar sesión para realizar el pedido',
        variant: 'destructive',
      });
      return { success: false };
    }

    if (cartItems.length === 0) {
      toast({
        title: 'Carrito vacío',
        description: 'Añade productos al carrito antes de realizar el pedido',
        variant: 'destructive',
      });
      return { success: false };
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/cart/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(customerData),
      });

      if (response.ok) {
        const data = await response.json();
        setCartItems([]);
        toast({
          title: '¡Pedido realizado!',
          description: `Se han creado ${data.orders_created} pedido(s). Recibirás un email de confirmación.`,
        });
        return { success: true, data };
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'No se pudo procesar el pedido',
          variant: 'destructive',
        });
        return { success: false, error };
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      toast({
        title: 'Error',
        description: 'Error de conexión durante el checkout',
        variant: 'destructive',
      });
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Check if product is in cart
  const isInCart = (productId) => {
    return cartItems.some(item => item.product_id === productId);
  };

  // Get cart totals
  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price || 0), 0);
  };

  const cartCount = cartItems.length;

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        loading,
        addToCart,
        removeFromCart,
        updateItemPriceType,
        clearCart,
        checkout,
        isInCart,
        getCartTotal,
        fetchCart,
        appliedCoupon,
        setAppliedCoupon,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
